/**
 * MessageRow
 * Fila expandible que muestra una notificacion push del cliente.
 * Colapsada: chevron + dot de no-leido + timestamp + badge subcategoria + titulo + preview.
 * Expandida: cuerpo completo + imagen + metadatos en texto muted.
 */
import { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Circle,
  Clock,
  AlertCircle,
  CheckCircle2,
  CreditCard,
  Zap,
  TrendingUp,
  MessageCircle,
} from 'lucide-react';
import type { MessageLogEntry, MessageCorrelationKind, CreditSubCategory } from '@/types/expedientes.types';

// Etiquetas en español para subcategorías de crédito
const SUB_CAT_LABEL: Record<CreditSubCategory, string> = {
  disbursement:    'Desembolso',
  eligibility:     'Elegibilidad',
  level_up:        'Subida de nivel',
  reminder:        'Recordatorio',
  payment:         'Pago',
  default_warning: 'Aviso mora',
  paid_off:        'Liquidación',
  lien:            'Gravamen',
  unknown:         'Crédito',
};

// Icono por tipo de correlacion con evento crediticio
const CHIP_ICON: Record<MessageCorrelationKind, React.ReactElement> = {
  payment_due:          <Clock size={12} />,
  payment_received:     <CheckCircle2 size={12} />,
  payment_missed:       <AlertCircle size={12} />,
  disbursement:         <CreditCard size={12} />,
  contract_signed:      <Zap size={12} />,
  credit_status_change: <TrendingUp size={12} />,
};

interface Props {
  entry: MessageLogEntry;
}

export function MessageRow({ entry }: Props) {
  const [expanded, setExpanded] = useState(false);
  const isUnread = entry.status === 'UNREAD';
  const isCredit = entry.category === 'credit';

  // Máximo 3 chips de correlación por especificación
  const chips = entry.correlations?.slice(0, 3) ?? [];

  return (
    <div className="border-b border-border last:border-b-0">
      {/* Fila colapsada */}
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full text-left px-3 py-2 hover:bg-muted transition flex items-start gap-2"
      >
        {/* Chevron de expansión */}
        {expanded
          ? <ChevronDown size={14} className="mt-1 shrink-0 text-muted-foreground" />
          : <ChevronRight size={14} className="mt-1 shrink-0 text-muted-foreground" />
        }

        {/* Dot de estado no-leido */}
        <Circle
          size={8}
          className={`mt-2 shrink-0 ${isUnread ? 'fill-primary text-primary' : 'text-muted-foreground'}`}
        />

        <div className="flex-1 min-w-0">
          {/* Timestamp + badge subcategoria o marker wallet */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{formatDate(entry.createdAt)}</span>
            {isCredit && entry.subCategory && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-600 dark:text-blue-400">
                {SUB_CAT_LABEL[entry.subCategory]}
              </span>
            )}
            {!isCredit && (
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <MessageCircle size={10} /> wallet
              </span>
            )}
          </div>

          {/* Título */}
          <div className="text-sm font-medium text-foreground truncate">
            {entry.title || '(sin título)'}
          </div>

          {/* Preview del body */}
          <div className="text-sm text-muted-foreground truncate">{entry.body}</div>

          {/* Chips correlacionales (max 3) */}
          {isCredit && chips.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {chips.map((c, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                  title={`${c.deltaMinutes >= 0 ? '+' : ''}${c.deltaMinutes} min`}
                >
                  {CHIP_ICON[c.kind]}
                  {c.description}
                </span>
              ))}
            </div>
          )}
        </div>
      </button>

      {/* Panel expandido */}
      {expanded && (
        <div className="px-8 pb-3 space-y-2 text-sm">
          {/* Cuerpo completo con saltos de línea preservados */}
          <div className="whitespace-pre-wrap text-foreground">{entry.body}</div>

          {/* Imagen de notificación si existe y no es placeholder */}
          {entry.notificationImage && entry.notificationImage !== 'NO_DATA' && (
            <img
              src={entry.notificationImage}
              alt=""
              className="h-16 w-16 object-contain rounded border border-border"
            />
          )}

          {/* Metadatos muted */}
          <div className="text-xs text-muted-foreground">
            Estado: {entry.status} · Canal: push in-app · ID:{' '}
            <span className="font-mono">{entry.id}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function formatDate(epochMs: number): string {
  try {
    return new Date(epochMs).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return String(epochMs);
  }
}
